import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import styles from "./About.module.scss";

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  // Animation variants for different elements
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const textVariants = {
    hidden: {
      opacity: 0,
      y: 30,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const cardVariants = {
    hiddenLeft: {
      opacity: 0,
      x: -60,
      y: 20,
    },
    hiddenRight: {
      opacity: 0,
      x: 60,
      y: 20,
    },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.section
      id="about"
      ref={ref}
      className={`${styles.sectionPadding} section-padding relative`}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className={styles.heroBg}>
        <div className={styles.heroBgImg}>
          <img src="/bannerBg1.webp" alt="HeroBgImg" />
        </div>
        <div className={styles.heroBgImg}>
          <img src="/bannerBg2.webp" alt="HeroBgImg" />
        </div>
        <div className={styles.heroBgImg}>
          <img src="/bannerBg3.webp" alt="HeroBgImg" />
        </div>
      </div>
      <div className="container-custom relative z-[2]">
        <motion.div className={styles.aboutHeading} variants={textVariants}>
          What We Do
        </motion.div>

        <motion.div
          className="flex justify-center items-center w-full"
          variants={textVariants}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-8 md:w-10 lg:w-12 h-auto"
            viewBox="0 0 34 2"
            fill="none"
          >
            <path
              d="M1 1H33"
              stroke="url(#paint0_linear_4077_8241)"
              strokeLinecap="round"
            />
            <defs>
              <linearGradient
                id="paint0_linear_4077_8241"
                x1="1"
                y1="2"
                x2="33"
                y2="2"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#80E5FF" />
                <stop offset="1" stopColor="#7584D6" />
              </linearGradient>
            </defs>
          </svg>
        </motion.div>

        <motion.div
          className="flex justify-center mt-2"
          variants={textVariants}
        >
          <div className="max-w-[48.3rem] text-center font-manrope text-5xl md:text-[5.6rem] font-semibold leading-[120%] tracking-[-0.28rem] bg-gradient-to-r from-[#80E5FF] to-[#545C9E] bg-clip-text text-transparent">
            Losing selling time?
          </div>
        </motion.div>

        <motion.div
          className="flex justify-center mt-8"
          variants={textVariants}
        >
          <p className={styles.aboutHeadDetails}>
            Stop juggling inboxes, updating CRMs, and chasing context across
            tabs.
          </p>
        </motion.div>

        <motion.div
          className="flex justify-center mt-8"
          variants={textVariants}
        >
          <p className={styles.aboutHeadDetailsHead}>
            Vertik.ai is an always-on, role-aware AI sales companion that
            connects your entire sales ecosystem into one intelligent,
            action-driven workspace.
          </p>
        </motion.div>

        <div
          className={`${styles.aboutCards} flex flex-wrap sm:flex-wrap md:flex-wrap`}
        >
          <motion.div
            className={styles.aboutCard}
            variants={cardVariants}
            initial="hiddenLeft"
            animate={isInView ? "visible" : "hiddenLeft"}
            transition={{ delay: 0.8 }}
          >
            <img src="/Title.png" alt="card-icon" />
            <div className={styles.cardBody}>
              <p className={styles.bodyTitle}> Proactive companion</p>
              <p className={styles.bodyDescription}>
                {" "}
                Plans your day, drafts, schedules, and executes with you.
              </p>
            </div>
          </motion.div>

          <motion.div
            className={styles.aboutCard}
            variants={cardVariants}
            initial="hiddenRight"
            animate={isInView ? "visible" : "hiddenRight"}
            transition={{ delay: 1.0 }}
          >
            <img src="/Personalization.png" alt="card-icon" />
            <div className={styles.cardBody}>
              <p className={styles.bodyTitle}> Intelligent guidance</p>
              <p className={styles.bodyDescription}>
                Next-best actions, risk flags, and nudges that keep deals
                moving.
              </p>
            </div>
          </motion.div>

          <motion.div
            className={styles.aboutCard}
            variants={cardVariants}
            initial="hiddenLeft"
            animate={isInView ? "visible" : "hiddenLeft"}
            transition={{ delay: 1.2 }}
          >
            <img src="/Strategy.png" alt="card-icon" />
            <div className={styles.cardBody}>
              <p className={styles.bodyTitle}> Connected ecosystem</p>
              <p className={styles.bodyDescription}>
                Email, CRM, meetings, tasks—one live, bi-directional customer
                view.
              </p>
            </div>
          </motion.div>

          <motion.div
            className={styles.aboutCard}
            variants={cardVariants}
            initial="hiddenRight"
            animate={isInView ? "visible" : "hiddenRight"}
            transition={{ delay: 1.4 }}
          >
            <img src="/AnalyzeAndScale.png" alt="card-icon" />
            <div className={styles.cardBody}>
              <p className={styles.bodyTitle}>Automated flow</p>
              <p className={styles.bodyDescription}>
                Prep, logging, follow-ups, outreach—handled automatically;
                nothing slips.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default About;
